import LegalPageLayout from "@/components/legal/LegalPageLayout";

const AiPolicy = () => {
  return (
    <LegalPageLayout title="AI Use Policy" effectiveDate="April 30, 2026" version="1.2">
      <h2>1. Purpose</h2>
      <p>
        This AI Use Policy explains how Young's Digital Atelier LLC ("Wavlynq") uses artificial
        intelligence in the Services and what you can expect. It supplements the
        <a href="/privacy"> Privacy Policy</a> and the <a href="/terms">Terms and Conditions</a>.
      </p>
      <p>
        Inside Wavlynq, our AI assistant is named <strong>RAELA</strong>. RAELA is the only
        AI-facing brand you will see across every sibling app (Fryqnc, Pulsynq, MindLynq, and the
        v2 apps as they launch — Spiritsynq, Jadesynq, Deafwav, Ablelynq, Fullynq, Openlynq).
      </p>

      <h2>2. Where RAELA Is Used</h2>
      <ul>
        <li><strong>Compatibility ranking</strong> in discovery, including the optional RESONATE communication-style matching available on MindLynq.</li>
        <li><strong>Discovery sort order</strong> — AMPLIFY boosts (paid promotion of your own profile) appear first, followed by Priority Matching, followed by standard ranking. RAELA is used only inside the standard ranking layer.</li>
        <li><strong>Safety classifiers</strong> that flag potential harassment, threats, sexual content involving minors, and crisis-indicator language for human-moderator review.</li>
        <li><strong>RAELA concierge and conversation suggestions</strong> shown to you in the app — you choose whether to use them.</li>
        <li><strong>Ask RAELA</strong> on the FAQ and Help pages — answers questions about how Wavlynq works.</li>
        <li><strong>Fraud and abuse detection</strong> — pattern detection on metadata to spot spam accounts, bots, and policy violations.</li>
        <li><strong>Mentor support tooling</strong> — summaries and suggestions surfaced to mentors only, never to other members.</li>
      </ul>

      <h2>3. About RAELA</h2>
      <p>
        RAELA is operated by Wavlynq through an internal abstraction (<code>aiClient</code>) that
        routes to the underlying model technology. The specific model provider is an internal
        implementation detail and may change over time as we improve safety, accuracy, and
        performance. Any change to RAELA's underlying technology will not change the protections in
        this Policy. Material changes that affect what data RAELA can access or how it is used will
        be announced in-app or by email.
      </p>

      <h2>4. What RAELA Will Not Do</h2>
      <ul>
        <li>RAELA does not generate fake profiles, fake messages, or impersonations of real people.</li>
        <li>Members may not use RAELA or any AI to misrepresent themselves as human in conversation. Section 7 of the Terms prohibits this.</li>
        <li>RAELA does not make solely automated decisions that produce legal or similarly significant effects on you. Material moderation actions (account suspension, mentor removal, KYC or BGC failure handling) involve human review.</li>
        <li>RAELA is not a replacement for professional medical, legal, or mental-health advice.</li>
      </ul>

      <h2>5. Data and Training</h2>
      <p>
        <strong>By default we do not use the contents of your direct messages, voice notes, or
        private profile fields to train AI models.</strong> Our service-provider agreements
        prohibit any underlying model provider from using your data to train their models.
        Aggregated, de-identified usage statistics may be used to improve our own ranking and
        safety systems.
      </p>

      <h3>5.1 AI Training Contribution (opt-in, default OFF)</h3>
      <p>
        During onboarding and any time later in <em>Settings → AI</em>, you can choose to enable{" "}
        <strong>AI Training Contribution</strong>. When ON, the corrections you make to RAELA's
        suggestions — edits, dismissals with feedback, and thumbs-down with a reason — may be used
        to improve RAELA. This setting is <strong>off by default</strong>, requires an explicit
        toggle, can be turned off at any time, and is recorded with a timestamp on your account.
        Turning it off does not retroactively delete corrections already used to improve RAELA, but
        we will not use any new corrections from your account after that point.
      </p>

      <h2>6. Crisis Detection, Emergency Button, and Safety Guardrails</h2>
      <p>
        RAELA's safety classifiers run server-side across direct messages, community chat, and the
        RAELA concierge in every sibling app at all times. When content contains crisis indicators
        (self-harm, suicidal ideation, statements of danger, expressions of hopelessness, domestic
        violence), the system surfaces our <strong>Crisis Resources</strong> and the{" "}
        <strong>Emergency Button</strong> immediately, before any other response. These safety
        features are <strong>free on every tier in every app</strong> and will never be paywalled
        or disabled by any user, admin, or setting.
      </p>
      <p>
        The Emergency Button is locale-aware. Its appearance is member-configurable but its function
        is hardcoded — it dials the appropriate local emergency number and, where you have enabled
        it, may begin a short recording. A recording started this way can only be stopped by the
        pressing member or by Wavlynq's founder contact.
      </p>
      <p>
        Guardrails are designed to fail safe: when uncertain, the system escalates to a human
        moderator and surfaces resources rather than suppressing them. RAELA is not a substitute for
        professional mental-health care. If you are in crisis, please contact a hotline or emergency
        services.
      </p>

      <h2>7. Neurodivergent Considerations (MindLynq)</h2>
      <p>
        MindLynq serves a neurodivergent community. RAELA features in MindLynq are designed to:
      </p>
      <ul>
        <li>Respect communication-style preferences captured via RESONATE (preferred pace, format, sensory considerations).</li>
        <li>Avoid pathologizing language and avoid prescribing therapy.</li>
        <li>Default to clear, literal phrasing in suggestions and reduce idiomatic or sarcastic outputs.</li>
        <li>Surface accommodations and disclosures only with the member's consent.</li>
        <li>Use heightened crisis-detection sensitivity thresholds.</li>
      </ul>

      <h2>8. Your Choices</h2>
      <ul>
        <li>You may turn off RAELA-assisted prompts and suggestions in Settings.</li>
        <li>You may turn AI Training Contribution off at any time in <em>Settings → AI</em>.</li>
        <li>You may request not to be subject to AI-assisted ranking by emailing <a href="mailto:privacy@wavlynq.com">privacy@wavlynq.com</a>; standard discovery (without AI ranking) will be used instead.</li>
        <li>You may export the data we hold about you, including the inputs and outputs of RAELA features tied to your account, at any time.</li>
        <li>EEA/UK/CH users have the right to object to processing based on legitimate interests.</li>
      </ul>

      <h2>9. Transparency and Reporting</h2>
      <p>
        We will publish updates here when we materially change how RAELA is used in the Services or
        learn of a significant safety incident. Report suspected AI misuse to{" "}
        <a href="mailto:support@wavlynq.com">support@wavlynq.com</a>.
      </p>

      <h2>10. Changes to this Policy</h2>
      <p>
        We will post any changes here and update the Effective Date. Material changes will be
        announced in-app or by email at least 14 days before they take effect.
      </p>

      <h2>11. Contact</h2>
      <p>
        Young's Digital Atelier LLC<br />
        702 Richland Hills Dr #760866<br />
        San Antonio, TX 78245, USA<br />
        AI questions: <a href="mailto:privacy@wavlynq.com">privacy@wavlynq.com</a>
      </p>
    </LegalPageLayout>
  );
};

export default AiPolicy;
